// The defense towers: TowerGob (a structure with a rotating turret, primary/secondary
// targeting, radar+power-gated smart targeting) and the GunTower (3-burst bullets at
// damage/3) / RocketTower (full-damage rocket; rate check FIRST) pair.
// game/Tower.cpp (all 610 lines).

import type { SimContext } from '../context.ts';
import { markDeadSpan } from '../sm/stream.ts';
import { Gob } from './gob.ts';
import { StructGob } from './struct.ts';
import { UnitGob, IsGobWithinRange } from './unitgob.ts';
import {
  TowerConsts,
  InitStructConstsClass,
  kfUntcNotifyEnemyNearby,
  kfUntcNotifyPowerLowHigh,
  kwcTile16th,
} from './unitconsts.ts';
import { kgtMachineGunTower, kgtRocketTower } from './gobtypes.ts';
import { kutMachineGunTower, kutRocketTower, kutRadar } from '../player/side.ts';
import { kumReactor, kumRadar } from '../player/masks.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  newMessage,
  knHandled,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedUpdate,
  kmidEnemyNearby,
  kmidAttackCommand,
  kmidFire,
  kmidFireComplete,
  kmidHit,
  kgidNull,
  kstIdle,
  kstAttack,
  kstDying,
} from '../sm/message.ts';
import {
  kfAniIgnoreFirstAdvance,
  kfAniResetWhenDone,
  kctUpdate,
  Animation,
} from '../anim/animation.ts';
import { CalcDir16, kdirS } from '../math/dir.ts';
import { WcTrunc, WcFromPc32 } from '../math/coords.ts';
import { idiv } from '../math/int32.ts';
import { kwxInvalid } from './mobileunit.ts';
import { CreateBulletGob, CreateRocketGob } from './shots.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxMachineGunTowerAbortRepair,
  ksfxMachineGunTowerAttack,
  ksfxMachineGunTowerDamaged,
  ksfxMachineGunTowerDestroyed,
  ksfxMachineGunTowerFire,
  ksfxMachineGunTowerRepair,
  ksfxMachineGunTowerSelect,
  ksfxRocketTowerAbortRepair,
  ksfxRocketTowerAttack,
  ksfxRocketTowerDamaged,
  ksfxRocketTowerDestroyed,
  ksfxRocketTowerFire,
  ksfxRocketTowerImpact,
  ksfxRocketTowerRepair,
  ksfxRocketTowerSelect,
} from '../data/soundeffects.ts';
import type { WPoint } from '../types.ts';

// game/Tower.cpp:5 — the tower atlases lay out 3 base strips, then the 16 turret
// strips at indices 3..18 (then icon/help). The identity table was a port bug: towers
// animated base strips and Fire(frame 1) crashed on the 1-frame strips (caught by the
// m_05 savegame cross-validation run — no corpus game had ever fired a tower).
const s_anGunTowerTurretStripIndices = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

// game/Tower.cpp:11-22 — common tower InitClass
function InitTowerClass(ctx: SimContext, c: TowerConsts, pini: IniReader): boolean {
  if (!InitStructConstsClass(ctx, c, pini)) return false;
  c.anFiringStripIndices = s_anGunTowerTurretStripIndices;
  c.wf |= kfUntcNotifyEnemyNearby | kfUntcNotifyPowerLowHigh;
  return true;
}

export abstract class TowerGob extends StructGob {
  m_tLastFire = 0;
  m_gidTargetPrimary = kgidNull;
  m_gidTargetSecondary = kgidNull;
  m_wptTarget: WPoint = { wx: kwxInvalid, wy: 0 };
  m_dir16Turret = kdirS * 2;
  m_aniTurret = new Animation();

  get m_ptwrc(): TowerConsts {
    return this.m_puntc as TowerConsts;
  }

  // game/Tower.cpp:29-41
  constructor(ctx: SimContext, ptwrc: TowerConsts) {
    super(ctx, ptwrc);
    this.m_aniTurret.Init(ctx.animDataFor(ptwrc.szAniName));
    this.SetAnimationStrip(this.m_aniTurret, ptwrc.anFiringStripIndices[this.m_dir16Turret]);
  }

  // game/Tower.cpp:72-76
  override Activate(): void {
    this.NotifyEnemyNearby();
    super.Activate();
  }

  // game/Tower.cpp:121-125 — towers are not takeoverable
  override IsTakeoverable(_pplr: Player): boolean {
    return false;
  }

  // game/Tower.cpp:127-135 — bypass StructGob's always-false
  override IsValidTarget(pgobTarget: Gob): boolean {
    return UnitGob.prototype.IsValidTarget.call(this, pgobTarget);
  }

  // game/Tower.cpp:137-166
  override SetTarget(
    gid: number,
    _wx: number = 0,
    _wy: number = 0,
    _wxC: number = 0,
    _wyC: number = 0,
    _tcR: number = 0,
    _wcM: number = 0,
  ): void {
    const pgobTarget = this.ctx.gobm.GetGob(gid);
    if (pgobTarget === null) return;

    if (this.IsAlly(pgobTarget.GetSide())) return;

    // (flash gated on the local player's perspective: presentation)
    if (
      this.ctx.plrLocal !== null &&
      !(pgobTarget as UnitGob).IsAlly(this.ctx.plrLocal.GetSide())
    ) {
      pgobTarget.Flash();
    }

    const msgT = newMessage(kmidAttackCommand, this.m_gid, this.m_gid);
    msgT.AttackCommand.wptTarget.wx = 0;
    msgT.AttackCommand.wptTarget.wy = 0;
    msgT.AttackCommand.gidTarget = gid;
    this.ctx.cmdq.Enqueue(msgT);
  }

  // game/Tower.cpp:168-208 — point the turret at the target (special-point muzzle)
  // game/Tower.cpp (knVerTowerGobState 1)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_dir16Turret = pstm.ReadByte();
    this.m_aniTurret.Init(this.ctx.animDataFor(this.m_ptwrc.szAniName));
    this.SetAnimationStrip(this.m_aniTurret, this.m_ptwrc.anFiringStripIndices[this.m_dir16Turret]);
    this.m_gidTargetPrimary = pstm.ReadWord();
    this.m_gidTargetSecondary = pstm.ReadWord();
    this.m_tLastFire = this.ctx.sim!.GetTickCount() - pstm.ReadDword();
    this.m_wptTarget.wx = (pstm.ReadWord() << 16) >> 16;
    this.m_wptTarget.wy = (pstm.ReadWord() << 16) >> 16;
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_dir16Turret);
    pstm.WriteWord(this.m_gidTargetPrimary);
    pstm.WriteWord(this.m_gidTargetSecondary);
    pstm.WriteDword(this.ctx.sim!.GetTickCount() - this.m_tLastFire);
    pstm.WriteWord(this.m_wptTarget.wx);
    pstm.WriteWord(this.m_wptTarget.wy);
    markDeadSpan(pstm, 2); // the ctor inits only .wx (Tower.cpp:34) (B20)
    return super.SaveState(pstm);
  }

  override DefUpdate(): void {
    const ptSpecial32 = this.m_ani.GetSpecialPoint32();
    const wx = this.m_wx + WcFromPc32(ptSpecial32.x);
    const wy = this.m_wy + WcFromPc32(ptSpecial32.y);

    if (this.m_wptTarget.wx !== kwxInvalid) {
      // Target in our own tile: stop trying to look exactly at it
      if (
        WcTrunc(wx) !== WcTrunc(this.m_wptTarget.wx) ||
        WcTrunc(wy) !== WcTrunc(this.m_wptTarget.wy)
      ) {
        const dir16 = CalcDir16(this.m_wptTarget.wx - wx, this.m_wptTarget.wy - wy);

        let d = dir16 - this.m_dir16Turret;
        if (d !== 0) {
          if (d < -8) d = 1;
          else if (d > 8) d = -1;
          if (d < 0) this.m_dir16Turret--;
          else this.m_dir16Turret++;
          this.m_dir16Turret = (this.m_dir16Turret >>> 0) % 16;
          this.SetAnimationStrip(
            this.m_aniTurret,
            this.m_ptwrc.anFiringStripIndices[this.m_dir16Turret],
          );
          this.m_unvl.MinSkip();
        }
      }
    }

    this.AdvanceAnimation(this.m_aniTurret);

    super.DefUpdate();
  }

  // game/Tower.cpp:210-219 — smart targeting requires a radar and power
  override FindEnemyNearby(tcRange: number): UnitGob | null {
    if (
      !(this.m_pplr as Player).IsPowerLow() &&
      (this.m_pplr as Player).GetUnitCount(kutRadar) > 0
    ) {
      return super.FindEnemyNearby(tcRange);
    }
    return null;
  }

  /** GunTower/RocketTower provide the actual weapon (ht.h's virtual Fire). */
  abstract Fire(puntTarget: UnitGob, wx: number, wy: number, wdx: number, wdy: number): boolean;

  // game/Tower.cpp:221-385
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidEnemyNearby:
          this.RememberEnemyNearby(pmsg.EnemyNearby.gidEnemy);
          this.m_unvl.MinSkip();
          return knHandled;

        case kmidAttackCommand:
          this.m_gidTargetPrimary = pmsg.AttackCommand.gidTarget;
          this.SetState(kstAttack);
          return knHandled;

        case kmidFire: {
          let puntTarget = this.ctx.gobm.GetGob(this.m_gidTargetPrimary) as UnitGob | null;
          if (
            puntTarget === null ||
            !IsGobWithinRange(this, puntTarget, this.m_ptwrc.tcFiringRange)
          ) {
            puntTarget = this.ctx.gobm.GetGob(this.m_gidTargetSecondary) as UnitGob | null;
          }
          if (puntTarget !== null) {
            // Burst follow-up shot (2 sync-RNG draws)
            const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
            const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;

            const ptTurretSpecial32 = this.m_aniTurret.GetSpecialPoint32(1);
            const ptBaseSpecial32 = this.m_ani.GetSpecialPoint32();

            const wpt: WPoint = { wx: 0, wy: 0 };
            puntTarget.GetCenter(wpt);
            CreateBulletGob(
              this.ctx,
              this.m_wx + WcFromPc32(ptBaseSpecial32.x + ptTurretSpecial32.x),
              this.m_wy + WcFromPc32(ptBaseSpecial32.y + ptTurretSpecial32.y),
              wpt.wx + wdxRnd,
              wpt.wy + wdyRnd,
              idiv(this.GetDamageTo(puntTarget), 3),
              this.m_gid,
              puntTarget.GetId(),
            );
          }
          return knHandled;
        }

        case kmidHit:
          // Counter-target the assailant if not busy (needs radar + power)
          if (
            this.m_gidTargetSecondary === kgidNull &&
            !(this.m_pplr as Player).IsPowerLow() &&
            (this.m_pplr as Player).GetUnitCount(kutRadar) > 0
          ) {
            this.m_gidTargetSecondary = pmsg.Hit.gidAssailant;
            this.SetState(kstAttack);
          }
          return super.ProcessStateMachineMessage(st, pmsg);
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    //-----------------------------------------------------------------------
    if (st === kstIdle) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.m_wptTarget.wx = kwxInvalid;
          return knHandled;

        case kmidReservedUpdate: {
          const puntTarget = this.FindEnemyNearby(this.m_ptwrc.tcFiringRange);
          if (puntTarget !== null) {
            this.m_gidTargetSecondary = puntTarget.GetId();
            this.SetState(kstAttack);
          }
          this.DefUpdate();
          return knHandled;
        }
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstAttack) {
      if (pmsg.mid === kmidReservedUpdate) {
        // Low power = do nothing at all
        let puntTarget: UnitGob | null = null;
        if (!(this.GetOwner() as Player).IsPowerLow()) {
          // Primary (player-set) first
          if (this.m_gidTargetPrimary !== kgidNull) {
            puntTarget = this.ctx.gobm.GetGob(this.m_gidTargetPrimary) as UnitGob | null;
            if (puntTarget === null || !this.IsValidTarget(puntTarget)) {
              this.m_gidTargetPrimary = kgidNull;
              puntTarget = null;
            } else if (!IsGobWithinRange(this, puntTarget, this.m_ptwrc.tcFiringRange)) {
              puntTarget = null;
            }
          }

          if (puntTarget === null) {
            // Secondary in the meantime
            puntTarget = this.ctx.gobm.GetGob(this.m_gidTargetSecondary) as UnitGob | null;
            if (
              puntTarget === null ||
              !this.IsValidTarget(puntTarget) ||
              !IsGobWithinRange(this, puntTarget, this.m_ptwrc.tcFiringRange)
            ) {
              puntTarget = this.FindEnemyNearby(this.m_ptwrc.tcFiringRange);
              if (puntTarget !== null) this.m_gidTargetSecondary = puntTarget.GetId();
              else this.m_gidTargetSecondary = kgidNull;
            }
          }
        }

        if (puntTarget !== null) {
          const ptSpecial32 = this.m_ani.GetSpecialPoint32();
          puntTarget.GetAttackPoint(this.m_wptTarget);
          this.Fire(
            puntTarget,
            this.m_wptTarget.wx,
            this.m_wptTarget.wy,
            this.m_wptTarget.wx - (this.m_wx + WcFromPc32(ptSpecial32.x)),
            this.m_wptTarget.wy - (this.m_wy + WcFromPc32(ptSpecial32.y)),
          );
        } else {
          this.SetState(kstIdle);
        }

        this.DefUpdate();
        return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstDying) {
      if (pmsg.mid === kmidReservedUpdate) {
        return knHandled; // no animating through the level frames
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// game/Tower.cpp:431-449
export function InitGunTowerClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new TowerConsts().bind(ctx);
  c.gt = kgtMachineGunTower;
  c.ut = kutMachineGunTower;
  c.umPrerequisites = (kumReactor | kumRadar) >>> 0;
  // Sound effects (Tower.cpp:439-446)
  c.sfxAbortRepair = ksfxMachineGunTowerAbortRepair;
  c.sfxAttack = ksfxMachineGunTowerAttack;
  c.sfxDamaged = ksfxMachineGunTowerDamaged;
  c.sfxDestroyed = ksfxMachineGunTowerDestroyed;
  c.sfxFire = ksfxMachineGunTowerFire;
  c.sfxImpact = ksfxNothing;
  c.sfxRepair = ksfxMachineGunTowerRepair;
  c.sfxSelect = ksfxMachineGunTowerSelect;
  return InitTowerClass(ctx, c, pini);
}

export class GunTowerGob extends TowerGob {
  constructor(ctx: SimContext) {
    // Tower.cpp:456-458
    super(ctx, ctx.unitConsts[kutMachineGunTower] as TowerConsts);
  }

  // game/Tower.cpp:462-512 — facing first, frame-0 strip, 3-burst at damage/3
  Fire(puntTarget: UnitGob, wx: number, wy: number, wdx: number, wdy: number): boolean {
    const dir16Fire = CalcDir16(wdx, wdy);
    if (this.m_dir16Turret !== dir16Fire) {
      this.m_unvl.MinSkip();
      return false;
    }

    const t = this.ctx.sim!.GetTickCount();
    const ctWait = this.m_ptwrc.ctFiringRate;
    const ctRemaining = ctWait - (t - this.m_tLastFire);
    if (ctRemaining > 0) {
      this.m_unvl.MinSkip(idiv(ctRemaining + idiv(kctUpdate, 2), kctUpdate) - 1);
      return false;
    }

    this.m_tLastFire = t;

    this.StartAnimation(
      this.m_aniTurret,
      this.m_ptwrc.anFiringStripIndices[dir16Fire],
      0,
      kfAniResetWhenDone,
    );
    this.ctx.smm!.SendDelayedMsgIds(
      kmidFireComplete,
      this.m_aniTurret.GetRemainingStripTime(),
      this.m_gid,
      this.m_gid,
    );

    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;

    const ptTurretSpecial32 = this.m_aniTurret.GetSpecialPoint32(1);
    const ptBaseSpecial32 = this.m_ani.GetSpecialPoint32();

    CreateBulletGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptBaseSpecial32.x + ptTurretSpecial32.x),
      this.m_wy + WcFromPc32(ptBaseSpecial32.y + ptTurretSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      idiv(this.GetDamageTo(puntTarget), 3),
      this.m_gid,
      puntTarget.GetId(),
    );

    // Two more shots at slight delays
    this.ctx.smm!.SendDelayedMsgIds(kmidFire, kctUpdate * 2, this.m_gid, this.m_gid);
    this.ctx.smm!.SendDelayedMsgIds(kmidFire, kctUpdate * 4, this.m_gid, this.m_gid);

    this.ctx.host.PlaySfx(this.m_ptwrc.sfxFire);

    return true;
  }
}

// game/Tower.cpp:527-543
export function InitRocketTowerClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new TowerConsts().bind(ctx);
  c.gt = kgtRocketTower;
  c.ut = kutRocketTower;
  c.umPrerequisites = (kumReactor | kumRadar) >>> 0;
  // Sound effects (Tower.cpp:535-542)
  c.sfxAbortRepair = ksfxRocketTowerAbortRepair;
  c.sfxAttack = ksfxRocketTowerAttack;
  c.sfxDamaged = ksfxRocketTowerDamaged;
  c.sfxDestroyed = ksfxRocketTowerDestroyed;
  c.sfxFire = ksfxRocketTowerFire;
  c.sfxImpact = ksfxRocketTowerImpact;
  c.sfxRepair = ksfxRocketTowerRepair;
  c.sfxSelect = ksfxRocketTowerSelect;
  return InitTowerClass(ctx, c, pini);
}

export class RocketTowerGob extends TowerGob {
  constructor(ctx: SimContext) {
    // Tower.cpp:551-553
    super(ctx, ctx.unitConsts[kutRocketTower] as TowerConsts);
  }

  // game/Tower.cpp:564-608 — RATE CHECK FIRST then facing; frame-1 strip; full damage
  Fire(puntTarget: UnitGob, wx: number, wy: number, wdx: number, wdy: number): boolean {
    const t = this.ctx.sim!.GetTickCount();
    const ctWait = this.m_ptwrc.ctFiringRate;
    const ctRemaining = ctWait - (t - this.m_tLastFire);
    if (ctRemaining > 0) {
      this.m_unvl.MinSkip(idiv(ctRemaining + idiv(kctUpdate, 2), kctUpdate) - 1);
      return false;
    }

    const dir16Fire = CalcDir16(wdx, wdy);
    if (this.m_dir16Turret !== dir16Fire) return false;

    this.m_tLastFire = t;

    this.StartAnimation(
      this.m_aniTurret,
      this.m_ptwrc.anFiringStripIndices[dir16Fire],
      1,
      kfAniIgnoreFirstAdvance | kfAniResetWhenDone,
    );
    this.ctx.smm!.SendDelayedMsgIds(
      kmidFireComplete,
      this.m_aniTurret.GetRemainingStripTime(),
      this.m_gid,
      this.m_gid,
    );

    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptTurretSpecial32 = this.m_aniTurret.GetSpecialPoint32();
    const ptBaseSpecial32 = this.m_ani.GetSpecialPoint32();

    CreateRocketGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptBaseSpecial32.x + ptTurretSpecial32.x),
      this.m_wy + WcFromPc32(ptBaseSpecial32.y + ptTurretSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      this.GetDamageTo(puntTarget),
      this.m_gid,
      puntTarget.GetId(),
    );

    this.ctx.host.PlaySfx(this.m_ptwrc.sfxFire);

    return true;
  }
}

// self-register (see factory.ts)
registerGobType(kgtMachineGunTower, (ctx) => new GunTowerGob(ctx));
registerGobType(kgtRocketTower, (ctx) => new RocketTowerGob(ctx));
